import { type FC, useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { Link } from 'dibs-elements/exports/Link';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { trackDesignFeature } from 'dibs-interiors-photos/exports/trackingHelpers';
import Header from './Header';

import styles from './ProjectViewMoreContainer.scss';

import {
    type ProjectViewMoreContainer_searchResult$key,
    type ProjectViewMoreContainer_searchResult$data,
} from './__generated__/ProjectViewMoreContainer_searchResult.graphql';

type DesignProject = ArrayElement<
    NonNullable<
        NonNullable<ProjectViewMoreContainer_searchResult$data['firm']>['publicProfile']
    >['designProjectsCover']
>;

type ProjectTileProps = {
    project: DesignProject;
};

const ProjectTile: FC<ProjectTileProps> = props => {
    const { project } = props;
    const { url, coverPhoto, name, addressDisplayString } = project || {};
    return (
        <Link
            href={url || ''}
            onClick={() => trackDesignFeature({ action: 'project click', label: 'project' })}
            className={styles.projectTile}
            dataTn="project-link"
            buyerLinkType="linkNoDecoration"
        >
            <div
                className={styles.projectImage}
                style={{
                    backgroundImage: `url("${coverPhoto || ''}")`,
                }}
            />
            <div className={styles.projectInfo}>
                {name} | {addressDisplayString}
            </div>
        </Link>
    );
};

type Props = {
    searchResult: ProjectViewMoreContainer_searchResult$key | null | undefined;
};

const ProjectViewMoreContainer: FC<Props> = ({ searchResult: searchResultRef }) => {
    const searchResult = useFragment(
        graphql`
            fragment ProjectViewMoreContainer_searchResult on TradeInteriorProjectQueryOutput {
                project {
                    serviceId
                }
                firm {
                    name
                    publicProfile {
                        designProjectsCover {
                            addressDisplayString
                            serviceId
                            name
                            url
                            coverPhoto
                        }
                    }
                }
            }
        `,
        searchResultRef
    );

    const { prevProject, nextProject } = useMemo(() => {
        const designProjects = searchResult?.firm?.publicProfile?.designProjectsCover || [];
        const currentProjectId = searchResult?.project?.serviceId || 0;

        const currentIndex = designProjects.findIndex(
            project => project?.serviceId === currentProjectId
        );

        let _nextProject = designProjects[currentIndex + 1] || null;

        if (!_nextProject && currentIndex !== 0) {
            _nextProject = designProjects[0];
        }

        const _prevProject = designProjects.find(project => {
            const projectId = project?.serviceId;
            return projectId !== _nextProject?.serviceId && projectId !== currentProjectId;
        });

        return { prevProject: _prevProject || null, nextProject: _nextProject };
    }, [searchResult]);

    if (!nextProject && !prevProject) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <Header
                title={
                    <FormattedMessage
                        id="abt.interiorsProject.moreProjects.label"
                        defaultMessage="More Projects by {firmName}"
                        values={{
                            firmName: searchResult?.firm?.name || '',
                        }}
                    />
                }
            />
            <GridRow>
                <div className={styles.colWrapper}>
                    {prevProject && (
                        <GridCol tabletLandscape={5} mobile={12}>
                            <ProjectTile project={prevProject} />
                        </GridCol>
                    )}
                    {nextProject && (
                        <GridCol tabletLandscape={5} mobile={12}>
                            <ProjectTile project={nextProject} />
                        </GridCol>
                    )}
                </div>
            </GridRow>
        </div>
    );
};

export default ProjectViewMoreContainer;
